import { get, set } from 'idb-keyval';

/**
 * v5 — Paramètres d'indicateur de scroll
 *
 * 3 zones indépendantes : panier, catégories, produits.
 * Chacune avec sa propre personnalisation.
 *
 * Comportement :
 * - Apparaît si scrollHeight > clientHeight (contenu dépasse)
 * - Disparaît dès que l'utilisateur a scrollé (>5px depuis le top)
 */

export type ScrollHintZone = 'cart' | 'categories' | 'products';

export type HintShape = 'pill' | 'rounded' | 'circle' | 'square';
export type HintAnimation = 'bounce' | 'pulse' | 'fade' | 'wiggle' | 'none';
export type HintTemplate =
  | 'orange'
  | 'discreet'
  | 'colorful'
  | 'minimal'
  | 'neon'
  | 'arrowOnly'
  | 'custom';

export interface ScrollHintConfig {
  enabled: boolean;
  template: HintTemplate;
  text: string;            // ex: "Voir plus", "↓ Scroll"
  showIcon: boolean;       // afficher l'icône flèche
  bgColor: string;         // couleur de fond
  textColor: string;       // couleur du texte
  borderColor: string;     // couleur bordure (peut être transparent)
  shape: HintShape;        // forme (pill, rounded, circle, square)
  animation: HintAnimation;// type d'animation
  size: 'sm' | 'md' | 'lg';// taille du badge
  glow: boolean;           // halo lumineux
}

export interface ScrollHintSettings {
  cart: ScrollHintConfig;
  categories: ScrollHintConfig;
  products: ScrollHintConfig;
}

const STORAGE_KEY = 'scrollHintSettings';

const DEFAULT_CONFIG: ScrollHintConfig = {
  enabled: true,
  template: 'orange',
  text: 'Voir plus',
  showIcon: true,
  bgColor: '#f97316',
  textColor: '#ffffff',
  borderColor: 'transparent',
  shape: 'pill',
  animation: 'bounce',
  size: 'md',
  glow: false,
};

export const DEFAULT_SCROLL_HINT_SETTINGS: ScrollHintSettings = {
  cart: { ...DEFAULT_CONFIG, text: 'Voir plus' },
  categories: { ...DEFAULT_CONFIG, text: 'Plus de catégories' },
  products: { ...DEFAULT_CONFIG, text: 'Plus de produits' },
};

/**
 * 5 templates pré-faits.
 */
export const HINT_TEMPLATES: Record<
  Exclude<HintTemplate, 'custom'>,
  { label: string; emoji: string; description: string; config: Partial<ScrollHintConfig> }
> = {
  orange: {
    label: 'Orange vif',
    emoji: '🟧',
    description: 'Pill orange & blanc',
    config: {
      template: 'orange',
      bgColor: '#f97316',
      textColor: '#ffffff',
      borderColor: 'transparent',
      shape: 'pill',
      animation: 'bounce',
      glow: false,
    },
  },
  discreet: {
    label: 'Discret',
    emoji: '⚫',
    description: 'Noir transparent',
    config: {
      template: 'discreet',
      bgColor: 'rgba(31,41,55,0.92)',
      textColor: '#ffffff',
      borderColor: 'transparent',
      shape: 'pill',
      animation: 'fade',
      glow: false,
    },
  },
  colorful: {
    label: 'Coloré',
    emoji: '🌈',
    description: 'Rose vif éclatant',
    config: {
      template: 'colorful',
      bgColor: '#ec4899',
      textColor: '#ffffff',
      borderColor: 'transparent',
      shape: 'pill',
      animation: 'pulse',
      glow: true,
    },
  },
  minimal: {
    label: 'Minimal',
    emoji: '⚪',
    description: 'Blanc & noir',
    config: {
      template: 'minimal',
      bgColor: '#ffffff',
      textColor: '#1f2937',
      borderColor: '#e5e7eb',
      shape: 'rounded',
      animation: 'wiggle',
      glow: false,
    },
  },
  neon: {
    label: 'Néon',
    emoji: '🎯',
    description: 'Halo lumineux',
    config: {
      template: 'neon',
      bgColor: '#06b6d4',
      textColor: '#ffffff',
      borderColor: 'transparent',
      shape: 'pill',
      animation: 'pulse',
      glow: true,
    },
  },
  arrowOnly: {
    label: 'Flèche seule',
    emoji: '⬇️',
    description: 'Sans texte, juste flèche',
    config: {
      template: 'arrowOnly',
      text: '',
      showIcon: true,
      bgColor: '#f97316',
      textColor: '#ffffff',
      borderColor: 'transparent',
      shape: 'circle',
      animation: 'bounce',
      glow: false,
    },
  },
};

export function applyHintTemplate(
  current: ScrollHintConfig,
  template: Exclude<HintTemplate, 'custom'>
): ScrollHintConfig {
  return {
    ...current,
    ...HINT_TEMPLATES[template].config,
    template,
  };
}

export async function getScrollHintSettings(): Promise<ScrollHintSettings> {
  try {
    const stored = await get<ScrollHintSettings>(STORAGE_KEY);
    if (stored && typeof stored === 'object') {
      return {
        cart: { ...DEFAULT_SCROLL_HINT_SETTINGS.cart, ...stored.cart },
        categories: { ...DEFAULT_SCROLL_HINT_SETTINGS.categories, ...stored.categories },
        products: { ...DEFAULT_SCROLL_HINT_SETTINGS.products, ...stored.products },
      };
    }
  } catch (e) {
    console.error('[ScrollHint] load error', e);
  }
  return DEFAULT_SCROLL_HINT_SETTINGS;
}

export async function saveScrollHintSettings(
  settings: ScrollHintSettings
): Promise<boolean> {
  try {
    await set(STORAGE_KEY, settings);
    window.dispatchEvent(
      new CustomEvent('scrollHintSettingsUpdated', { detail: settings })
    );
    return true;
  } catch (e) {
    console.error('[ScrollHint] save error', e);
    return false;
  }
}

/* ============================================
 * Helpers visuels
 * ============================================ */

export const SHAPE_CLASSES: Record<HintShape, string> = {
  pill: 'rounded-full',
  rounded: 'rounded-xl',
  circle: 'rounded-full aspect-square',
  square: 'rounded-md',
};

export const ANIMATION_CLASSES: Record<HintAnimation, string> = {
  bounce: 'scroll-hint-bounce',
  pulse: 'scroll-hint-pulse',
  fade: 'scroll-hint-fade',
  wiggle: 'scroll-hint-wiggle',
  none: '',
};

export const SIZE_CLASSES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'text-xs px-2.5 py-1',
  md: 'text-sm px-3.5 py-1.5',
  lg: 'text-base px-4 py-2',
};
